// The small centred dialogs of a lead's page: logging a note, call, visit or reply; changing the stage; marking do-not-contact and
// allowing contact again; deleting the lead; and turning it into an organisation.

import type { Lead, LeadLostReason, LeadStage } from '@clavian/api-client';
import { canMoveLeadStage } from '@clavian/domain/leads';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Dialog, Field, RadioGroup, SelectField, TextField, useToast } from '@clavian/ui-web';
import { useNavigate } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { Notice } from '../../ui';
import { organizationParams } from '../organizations/queries';
import { HQ_PATHS } from '../paths';
import { LEAD_LOST_REASON_ORDER, LEAD_STAGE_ORDER } from './labels';
import { useChangeLeadStage, useConvertLead, useDeleteLead, useLogLeadActivity, useUpdateLead } from './queries';
import { fromLocalInput, toLocalInput } from './when';

type Target = { readonly id: string; readonly businessName: string };

/** A multi-line input on the kit's field, as the kit has no textarea of its own. */
function TextArea({ label, description, error, value, onChange, required = false, rows = 4 }: { readonly label: string; readonly description?: string; readonly error?: string | undefined; readonly value: string; readonly onChange: (value: string) => void; readonly required?: boolean; readonly rows?: number }): ReactNode {
  return (
    <Field label={label} description={description} error={error} required={required} showOptional={!required}>
      {(control) => (
        <textarea
          id={control.id}
          aria-describedby={control['aria-describedby']}
          aria-invalid={control['aria-invalid']}
          className="clav-input console-hq-reason"
          rows={rows}
          maxLength={2000}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </Field>
  );
}

// -------------------------------------------------------------------------------------------------
// Log activity
// -------------------------------------------------------------------------------------------------

const LOGGABLE = ['note', 'call', 'visit', 'reply'] as const;
type Loggable = (typeof LOGGABLE)[number];

export function LogActivityDialog({ lead, onClose }: { readonly lead: Target | undefined; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <Dialog open={lead !== undefined} onOpenChange={(open) => (open ? undefined : onClose())} size="md" dirty="auto" title={t('leads.activity.title')} description={lead === undefined ? undefined : t('leads.activity.description', { name: lead.businessName })}>
      {lead === undefined ? null : <LogActivityForm lead={lead} onDone={onClose} />}
    </Dialog>
  );
}

function LogActivityForm({ lead, onDone }: { readonly lead: Target; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const log = useLogLeadActivity();
  const [kind, setKind] = useState<Loggable>('note');
  const [body, setBody] = useState('');
  const [when, setWhen] = useState(() => toLocalInput(Date.now()));
  const [errors, setErrors] = useState<{ body?: string; when?: string }>({});
  const [failure, setFailure] = useState<string | undefined>(undefined);

  const submit = async () => {
    const at = fromLocalInput(when);
    const found = {
      ...(kind === 'note' && body.trim() === '' ? { body: t('leads.activity.bodyRequired') } : {}),
      ...(at === undefined || new Date(at).getTime() > Date.now() + 60_000 ? { when: t('leads.activity.future') } : {}),
    };
    setErrors(found);
    if (Object.keys(found).length > 0 || at === undefined) return;
    setFailure(undefined);
    try {
      await log.mutateAsync({ leadId: lead.id, body: { kind, ...(body.trim() === '' ? {} : { body: body.trim() }), occurredAt: at } });
      show({ tone: 'ok', title: t('leads.activity.saved') });
      onDone();
    } catch (error) {
      setFailure(problems.text(error, { fallback: t('leads.activity.failed') }));
    }
  };

  return (
    <form
      className="console-form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      {failure === undefined ? null : (
        <Notice tone="danger" role="alert">
          {failure}
        </Notice>
      )}
      <SelectField label={t('leads.activity.kind')} options={LOGGABLE.map((one) => ({ value: one, label: t(`leads.activity.kinds.${one}`) }))} value={kind} onValueChange={(value) => setKind(value as Loggable)} />
      <TextArea label={t('leads.activity.body')} value={body} onChange={setBody} required={kind === 'note'} error={errors.body} />
      <TextField label={t('leads.activity.when')} type="datetime-local" value={when} error={errors.when} onChange={(event) => setWhen(event.target.value)} />
      <div className="console-form__actions">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('leads.activity.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={log.isPending}>
          {t('leads.activity.confirm')}
        </Button>
      </div>
    </form>
  );
}

// -------------------------------------------------------------------------------------------------
// Change stage
// -------------------------------------------------------------------------------------------------

export function ChangeStageDialog({ lead, onClose }: { readonly lead: (Target & { readonly stage: LeadStage }) | undefined; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <Dialog open={lead !== undefined} onOpenChange={(open) => (open ? undefined : onClose())} size="md" dirty="auto" title={t('leads.stageDialog.title')} description={lead === undefined ? undefined : t('leads.stageDialog.description', { name: lead.businessName, stage: t(`leads.stage.${lead.stage}`).toLowerCase() })}>
      {lead === undefined ? null : <ChangeStageForm lead={lead} onDone={onClose} />}
    </Dialog>
  );
}

function ChangeStageForm({ lead, onDone }: { readonly lead: Target & { readonly stage: LeadStage }; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const change = useChangeLeadStage();
  // The stage the dialog opened on: the lead's own copy changes the moment the move is answered, a beat before the dialog closes.
  const [from] = useState(lead.stage);
  const options = LEAD_STAGE_ORDER.filter((stage) => canMoveLeadStage(from, stage));
  const [stage, setStage] = useState<LeadStage>(options[0] ?? 'contacted');
  const [reason, setReason] = useState<LeadLostReason | undefined>(undefined);
  const [note, setNote] = useState('');
  const [failure, setFailure] = useState<string | undefined>(undefined);

  const submit = async () => {
    if (stage === 'lost' && reason === undefined) {
      setFailure(t('leads.stageDialog.reasonRequired'));
      return;
    }
    setFailure(undefined);
    try {
      await change.mutateAsync({ leadId: lead.id, body: { stage, ...(stage === 'lost' && reason !== undefined ? { lostReason: reason } : {}), ...(note.trim() === '' ? {} : { note: note.trim() }) } });
      show({ tone: 'ok', title: t('leads.pipeline.moved', { name: lead.businessName, stage: t(`leads.stage.${stage}`) }) });
      onDone();
    } catch (error) {
      setFailure(problems.text(error, { fallback: t('leads.stageDialog.failed') }));
    }
  };

  return (
    <form
      className="console-form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      {failure === undefined ? null : (
        <Notice tone="danger" role="alert">
          {failure}
        </Notice>
      )}
      <SelectField label={t('leads.stageDialog.stage')} options={options.map((one) => ({ value: one, label: t(`leads.stage.${one}`) }))} value={stage} onValueChange={(value) => setStage(value as LeadStage)} />
      {stage === 'lost' ? (
        <RadioGroup
          label={t('leads.stageDialog.reason')}
          name="lostReason"
          value={reason ?? ''}
          onValueChange={(value) => setReason(value as LeadLostReason)}
          options={LEAD_LOST_REASON_ORDER.map((one) => ({ value: one, label: t(`leads.lostReason.${one}`) }))}
        />
      ) : null}
      <TextArea label={t('leads.stageDialog.note')} description={t('leads.stageDialog.noteHint')} value={note} onChange={setNote} rows={3} />
      <div className="console-form__actions">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('leads.stageDialog.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={change.isPending}>
          {t('leads.stageDialog.confirm')}
        </Button>
      </div>
    </form>
  );
}

// -------------------------------------------------------------------------------------------------
// Do not contact
// -------------------------------------------------------------------------------------------------

/** Marking a lead do-not-contact asks why, in a small dialog (or, when it is already marked, whether to allow contact again). */
export function DoNotContactDialog({ lead, mode, onClose }: { readonly lead: Target; readonly mode: 'set' | 'clear' | undefined; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const update = useUpdateLead();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const setting = mode === 'set';

  const confirm = async () => {
    if (setting && reason.trim() === '') {
      setError(t('leads.lead.dnc.reasonRequired'));
      return;
    }
    setFailure(undefined);
    try {
      await update.mutateAsync({ leadId: lead.id, body: setting ? { doNotContact: true, doNotContactReason: reason.trim() } : { doNotContact: false } });
      show({ tone: 'ok', title: t(setting ? 'leads.lead.dnc.saved' : 'leads.lead.dnc.cleared') });
      setReason('');
      onClose();
    } catch (failed) {
      setFailure(problems.text(failed, { fallback: t('leads.lead.dnc.failed') }));
    }
  };

  return (
    <Dialog
      open={mode !== undefined}
      onOpenChange={(open) => (open ? undefined : onClose())}
      size="sm"
      title={t(setting ? 'leads.lead.dnc.reasonTitle' : 'leads.lead.dnc.clearTitle')}
      description={setting ? t('leads.lead.dnc.reasonDescription') : t('leads.lead.dnc.clearBody')}
    >
      <form
        className="console-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void confirm();
        }}
      >
        {failure === undefined ? null : (
          <Notice tone="danger" role="alert">
            {failure}
          </Notice>
        )}
        {setting ? <TextArea label={t('leads.lead.dnc.reason')} value={reason} onChange={setReason} required error={error} rows={3} /> : null}
        <div className="console-form__actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('leads.lead.dnc.cancel')}
          </Button>
          <Button type="submit" variant={setting ? 'primary' : 'secondary'} busy={update.isPending}>
            {t(setting ? 'leads.lead.dnc.confirm' : 'leads.lead.dnc.clearConfirm')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// -------------------------------------------------------------------------------------------------
// Delete
// -------------------------------------------------------------------------------------------------

export function DeleteLeadDialog({ lead, onClose, onDeleted }: { readonly lead: Target | undefined; readonly onClose: () => void; readonly onDeleted: () => void }): ReactNode {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const remove = useDeleteLead();
  const [failure, setFailure] = useState<string | undefined>(undefined);

  const confirm = async () => {
    if (lead === undefined) return;
    setFailure(undefined);
    try {
      await remove.mutateAsync(lead.id);
      show({ tone: 'ok', title: t('leads.lead.deleteDialog.deleted', { name: lead.businessName }) });
      onDeleted();
    } catch (error) {
      setFailure(problems.text(error, { fallback: t('leads.lead.deleteDialog.failed') }));
    }
  };

  return (
    <Dialog
      open={lead !== undefined}
      onOpenChange={(open) => (open ? undefined : onClose())}
      size="sm"
      title={t('leads.lead.deleteDialog.title')}
      description={lead === undefined ? undefined : t('leads.lead.deleteDialog.body', { name: lead.businessName })}
    >
      <div className="console-form">
        {failure === undefined ? null : (
          <Notice tone="danger" role="alert">
            {failure}
          </Notice>
        )}
        <div className="console-form__actions">
          <Button variant="ghost" onClick={onClose}>
            {t('leads.lead.deleteDialog.cancel')}
          </Button>
          <Button variant="danger" busy={remove.isPending} onClick={() => void confirm()}>
            {t('leads.lead.deleteDialog.confirm')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// -------------------------------------------------------------------------------------------------
// Convert
// -------------------------------------------------------------------------------------------------

type ContactChannel = 'phone' | 'email';

export function ConvertLeadDialog({ lead, onClose }: { readonly lead: Lead | undefined; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <Dialog open={lead !== undefined} onOpenChange={(open) => (open ? undefined : onClose())} size="md" dirty="auto" title={t('leads.convert.title')} description={t('leads.convert.description')}>
      {lead === undefined ? null : <ConvertForm lead={lead} onDone={onClose} />}
    </Dialog>
  );
}

function ConvertForm({ lead, onDone }: { readonly lead: Lead; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const navigate = useNavigate();
  const convert = useConvertLead();
  const [name, setName] = useState(lead.businessName);
  const [channel, setChannel] = useState<ContactChannel>(lead.email !== null ? 'email' : 'phone');
  const [phone, setPhone] = useState(lead.phone ?? '');
  const [email, setEmail] = useState(lead.email ?? '');
  const [errors, setErrors] = useState<{ name?: string; contact?: string }>({});
  const [failure, setFailure] = useState<string | undefined>(undefined);

  const submit = async () => {
    const contact = (channel === 'phone' ? phone : email).trim();
    const found = { ...(name.trim() === '' ? { name: t('leads.convert.nameRequired') } : {}), ...(contact === '' ? { contact: t('leads.convert.contactRequired') } : {}) };
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setFailure(undefined);
    try {
      const done = await convert.mutateAsync({ leadId: lead.id, body: { name: name.trim(), owner: channel === 'phone' ? { phoneNumber: contact } : { email: contact } } });
      show({ tone: 'ok', title: t('leads.convert.done', { name: done.organization.name }) });
      onDone();
      await navigate({ to: HQ_PATHS.organization, params: organizationParams(done.organization.id) });
    } catch (error) {
      const code = problemCode(error);
      if (code === PROBLEM.leadConverted) setFailure(t('leads.convert.converted'));
      else if (code === PROBLEM.slugTaken) setFailure(t('leads.convert.slugTaken'));
      else setFailure(problems.text(error, { fallback: t('leads.convert.failed') }));
    }
  };

  return (
    <form
      className="console-form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      {failure === undefined ? null : (
        <Notice tone="danger" role="alert">
          {failure}
        </Notice>
      )}
      <TextField label={t('leads.convert.name')} required value={name} error={errors.name} onChange={(event) => setName(event.target.value)} />
      <RadioGroup
        label={t('leads.convert.owner')}
        name="ownerChannel"
        orientation="horizontal"
        value={channel}
        onValueChange={(value) => setChannel(value as ContactChannel)}
        options={[
          { value: 'email', label: t('leads.convert.byEmail') },
          { value: 'phone', label: t('leads.convert.byPhone') },
        ]}
      />
      {channel === 'phone' ? (
        <TextField label={t('leads.convert.phone')} required type="tel" value={phone} error={errors.contact} onChange={(event) => setPhone(event.target.value)} />
      ) : (
        <TextField label={t('leads.convert.email')} required type="email" value={email} error={errors.contact} onChange={(event) => setEmail(event.target.value)} />
      )}
      <div className="console-form__actions">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('leads.convert.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={convert.isPending}>
          {t('leads.convert.confirm')}
        </Button>
      </div>
    </form>
  );
}
